'use client';

import { useEffect, useRef } from 'react';
import { comparePlayVersions, normalizePlay, type SyncedPlay } from './syncedPlay';
import { activePlay } from './syncedViews';
import { withElapsedPlayTime, type GameSavePayload } from './savePolicy';

/*
  Persisting a play into the client's synced store: every change goes into the store, and the platform's
  sync carries it to the server. Nothing here talks to the network. Shared by the app and the website,
  which differ only in where plays are kept and how the player comes and goes (PlaySavePlatform).

  Which play a change belongs to:
  - The board's active play, while it's unfinished. Clearing an unfinished board is still the same attempt.
  - A new play once that one is finished: a completed play is frozen, and the next attempt (after Play
    Again) is a play of its own, created by its first move rather than by the reset.

  Returning to the board (the app to the foreground, the tab to view) is the one moment an open board takes
  changes from elsewhere: the sync that starts then may bring a newer version of it, from another device.
  So does a board opened while a sync is out, which opens at once and takes what that sync brings. The
  board settles the way the server will settle it anyway once this device's moves arrive: if the version
  the sync brought outranks the board (moves made since included), it replaces the board; otherwise the
  board's moves stand and are saved. See docs/client-synced-database.md.
*/

/** Matches the debounce during normal play; a completion bypasses it. */
const DEBOUNCE_MS = 500;

/** Where one player's plays are kept, and how the player comes and goes, on this client. */
export interface PlaySavePlatform {
  getPlay(cid: string): SyncedPlay | null;
  playsForBoard(boardId: number): SyncedPlay[];
  /** `send` false keeps the play on this device: it's stored, but not pushed until written with true. */
  save(play: SyncedPlay, send: boolean): void;
  /** Syncs both ways: for when the player returns, and changes from elsewhere may be waiting. */
  syncNow(): Promise<void>;
  /** Syncs only if there's something to send: for completing or leaving a board, where a pull has nothing
   * to offer that the next return won't. */
  sendUnsent(): Promise<void>;
  /** The sync under way, if any, to wait on without asking for another. */
  syncInFlight(): Promise<void> | null;
  randomUUID(): string;
  /** Calls `onLeave` when the player stops looking (app backgrounded, tab hidden) and `onReturn` when they
   * come back; returns the unsubscribe. */
  subscribeVisibility(onLeave: () => void, onReturn: () => void): () => void;
}

export interface SyncedPlaySaveOptions {
  platform: PlaySavePlatform;
  board: { id: number; game_id: number };
  /** Null for a board that isn't a day's puzzle (a tutorial, a practice puzzle). */
  boardDate: string | null;
  /** The slug of a tutorial or practice board, where the client knows it; the server fills it in otherwise. */
  boardSlug?: string | null;
  /** The Monday of a weekly challenge board's week, so the play counts as one before the server says so. */
  boardWeek?: string | null;
  /** The play the board opened to, which further progress continues. */
  initial: SyncedPlay | null;
  /** The play clock, so leaving the board saves the time up to now; see withElapsedPlayTime. */
  elapsedSeconds: () => number;
  /** Shows the board's plays as the store now holds them, in place of what's on screen. */
  onReplace: (plays: SyncedPlay[]) => void;
  /** False for a board whose unfinished plays are worth nothing to the server (a tutorial): they're kept
   * on this device, and only the completion is sent. */
  sendIncomplete?: boolean;
}

/** What makes two payloads the same save. Not `savedAt`, which is stamped at the moment of writing. */
const contentKey = (p: GameSavePayload<unknown>) =>
  JSON.stringify([{ ...p.playData, savedAt: undefined }, p.moveHistory, p.complete, p.playDuration]);

export function useSyncedPlaySave<CellT>(payload: GameSavePayload<CellT> | null, options: SyncedPlaySaveOptions) {
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  /** The play on screen as last saved or loaded; null before the board has one. */
  const shownRef = useRef<SyncedPlay | null>(options.initial);
  /** The content last written, or seen when the board opened: only a change from it is saved. */
  const baselineRef = useRef<string | null>(null);
  const latestRef = useRef<GameSavePayload<CellT> | null>(null);
  /** Moves a replacement threw away, which a save already scheduled must not bring back. */
  const discardedRef = useRef<string | null>(null);

  /** The play a payload would be saved as, or null when it isn't one: nothing played yet, or a completion
   * already recorded. */
  const toPlay = useRef((next: GameSavePayload<CellT>): SyncedPlay | null => {
    const { platform, board, boardDate, boardSlug, boardWeek } = optionsRef.current;
    const shown = shownRef.current;
    const previous = shown ? platform.getPlay(shown.cid) : null;
    const startsNew = !previous || (previous.complete && !next.complete);
    // Opening a board, or resetting a finished one, isn't a play until the first move.
    if (startsNew && !next.complete && next.moveHistory.length === 0) return null;
    if (previous?.complete && next.complete) return null;

    const now = Date.now();
    return normalizePlay<unknown>({
      cid: startsNew ? platform.randomUUID() : previous.cid,
      boardId: board.id,
      gameId: board.game_id,
      boardDate,
      boardSlug: boardSlug ?? null,
      boardWeek: boardWeek ?? null,
      createdAt: startsNew ? now : previous.createdAt,
      complete: next.complete,
      completedAt: next.complete ? now : null,
      duration: next.playDuration,
      playData: { ...next.playData, savedAt: now },
      moves: next.moveHistory,
      seq: startsNew ? null : previous.seq,
    });
  }).current;

  const write = useRef((next: GameSavePayload<CellT>) => {
    const key = contentKey(next);
    if (key === baselineRef.current || key === discardedRef.current) return;
    baselineRef.current = key;

    const play = toPlay(next);
    if (!play) return;
    const { platform, sendIncomplete = true } = optionsRef.current;
    platform.save(play, play.complete || sendIncomplete);
    shownRef.current = play;
  }).current;

  /** Saves what's outstanding, with the time up to now, and sends it. For leaving the board or the page. */
  const flush = useRef(() => {
    const latest = latestRef.current;
    if (latest) write(withElapsedPlayTime(latest, optionsRef.current.elapsedSeconds()));
    void optionsRef.current.platform.sendUnsent();
  }).current;

  /** Once `sync` lands: shows what it brought, if that outranks what's on screen. */
  const settle = useRef(async (sync: Promise<void>) => {
    await sync;
    const { platform, board, onReplace } = optionsRef.current;

    const replace = () => {
      const plays = platform.playsForBoard(board.id);
      shownRef.current = activePlay(plays);
      // The board's next payload is the replacement itself, not a change to save.
      baselineRef.current = null;
      discardedRef.current = latestRef.current && contentKey(latestRef.current);
      onReplace(plays);
    };

    // Moves made while the sync was out: theirs against the store's version, as the server will judge.
    const latest = latestRef.current;
    if (latest && contentKey(latest) !== baselineRef.current) {
      const ours = toPlay(latest);
      if (ours) {
        const held = platform.getPlay(ours.cid);
        if (held && comparePlayVersions(held, ours) > 0) replace();
        else write(latest);
        return;
      }
    }

    // Nothing unsaved: the board shows what it last saved, so any difference came from elsewhere.
    const newest = activePlay(platform.playsForBoard(board.id));
    const shown = shownRef.current;
    if (newest && (!shown || newest.cid !== shown.cid || comparePlayVersions(newest, shown) !== 0)) replace();
  }).current;

  useEffect(() => {
    latestRef.current = payload;
    if (!payload) return;

    if (baselineRef.current === null) {
      baselineRef.current = contentKey(payload);
      // Re-rendering cancelled any save of the discarded moves still scheduled.
      discardedRef.current = null;
      return;
    }
    if (payload.complete) {
      write(payload);
      // Completions drive stats, results and other devices' week bars: send at once.
      void optionsRef.current.platform.sendUnsent();
      return;
    }
    const timer = setTimeout(() => write(payload), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [payload, write]);

  useEffect(
    () =>
      optionsRef.current.platform.subscribeVisibility(flush, () => void settle(optionsRef.current.platform.syncNow())),
    [flush, settle]
  );

  // A board opened while a sync is out opens with what the device has, and takes what the sync brings.
  useEffect(() => {
    const sync = optionsRef.current.platform.syncInFlight();
    if (sync) void settle(sync);
  }, [settle]);

  // Leaving the board — going home, opening another puzzle — saves and sends what's outstanding.
  useEffect(() => flush, [flush]);
}
